import AuthService from "services/AuthService.js";
import TokenStorage, { sessionCookieOptions } from "utils/TokenStorage.js";
import { bootstrapClientSession } from "utils/sessionBootstrap.js";
import Cookies from "universal-cookie";

const cookies = new Cookies();

const STAFF_ROLES = [
  "SUPER_ADMIN",
  "ORGANIZATION_ADMIN",
  "ASSISTANT",
  "DOCTOR",
  "HOSPITAL_ADMIN",
];

class Authenticator {
  static bootstrapClientConfig() {
    bootstrapClientSession();
  }

  static getUserRoles() {
    const roles = cookies.get("user_roles");
    if (Array.isArray(roles)) {
      return roles;
    }
    if (typeof roles === "string") {
      try {
        const parsed = JSON.parse(roles);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  static hasPendingRoleSelection() {
    if (cookies.get("start_view_path")) {
      return false;
    }
    const roles = this.getUserRoles();
    if (roles.length < 2) {
      return false;
    }
    const hasPatient = roles.includes("PATIENT");
    const hasStaff = roles.some((role) => STAFF_ROLES.includes(role));
    return hasPatient && hasStaff;
  }

  static getHomePath() {
    const startViewPath = cookies.get("start_view_path");
    if (startViewPath) {
      return startViewPath;
    }

    if (this.hasPendingRoleSelection()) {
      return "/account/role-selection";
    }

    const roleType = cookies.get("user_role_type");
    if (roleType === "PATIENT") {
      return "/patient";
    }
    if (roleType) {
      return "/hospital";
    }

    const roles = this.getUserRoles();
    if (roles.length === 1) {
      return roles[0] === "PATIENT" ? "/patient" : "/hospital";
    }

    return null;
  }

  static verifyCredentials(email, password, captchaToken) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await AuthService.verifyCredentials(
          { email, password },
          captchaToken
        );
        const transactionId = response.data.transactionId;
        resolve({
          transactionId,
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  static authenticate(email, password, otp, transactionId, captchaToken) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await AuthService.authenticate(
          { email, password, otp, transactionId },
          captchaToken
        );

        TokenStorage.setTokens(response.data);
        this.bootstrapClientConfig();

        const roles = response.data.roles;
        cookies.set("user_roles", roles, sessionCookieOptions());

        const hasPatient = roles.includes("PATIENT");
        const hasStaff = roles.some((role) => STAFF_ROLES.includes(role));
        const needsRoleSelection = roles.length > 1 && hasPatient && hasStaff;

        if (needsRoleSelection) {
          resolve({ needsRoleSelection: true });
        } else {
          cookies.set("user_role_type", roles[0], sessionCookieOptions());

          let navigateTo = "/hospital";
          if (roles.includes("PATIENT")) {
            navigateTo = "/patient";
          }

          cookies.set("start_view_path", navigateTo, sessionCookieOptions());

          resolve({ navigateTo });
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  static resendOTP(email, password, captchaToken) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await AuthService.resendOTP(
          { email, password },
          captchaToken
        );
        const transactionId = response.data.transactionId;
        resolve({
          transactionId,
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  static logout() {
    this.clearLocalSession();
  }

  static clearLocalSession() {
    this.clearCookies();
    window.location = "/";
  }

  static restoreSessionFromAccessToken(accessToken) {
    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      const roles = payload.roles;
      if (!Array.isArray(roles) || roles.length === 0) {
        return false;
      }

      const options = sessionCookieOptions();
      cookies.set("user_roles", roles, options);

      if (cookies.get("start_view_path")) {
        return true;
      }

      const hasPatient = roles.includes("PATIENT");
      const hasStaff = roles.some((role) => STAFF_ROLES.includes(role));
      if (roles.length > 1 && hasPatient && hasStaff) {
        return true;
      }

      const roleType = cookies.get("user_role_type") || roles[0];
      cookies.set("user_role_type", roleType, options);
      cookies.set(
        "start_view_path",
        roleType === "PATIENT" ? "/patient" : "/hospital",
        options
      );
      return true;
    } catch {
      return false;
    }
  }

  static isAuthenticated() {
    const hasToken =
      TokenStorage.getAccessToken() || TokenStorage.getRefreshToken();

    if (
      !hasToken ||
      (TokenStorage.isAccessTokenExpired() && !TokenStorage.getRefreshToken())
    ) {
      this.clearCookies();
      return false;
    }

    if (!this.getHomePath()) {
      const accessToken = TokenStorage.getAccessToken();
      if (accessToken && this.restoreSessionFromAccessToken(accessToken)) {
        return Boolean(this.getHomePath());
      }
      if (TokenStorage.getRefreshToken()) {
        return false;
      }
      this.clearCookies();
      return false;
    }

    return true;
  }

  static async tryRestoreSession() {
    if (this.isAuthenticated()) {
      return true;
    }

    const refreshToken = TokenStorage.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await AuthService.refresh(refreshToken);
      TokenStorage.setTokens(response.data);
      this.bootstrapClientConfig();
      const accessToken = TokenStorage.getAccessToken();
      if (accessToken) {
        this.restoreSessionFromAccessToken(accessToken);
      }
      return this.isAuthenticated();
    } catch {
      this.clearCookies();
      return false;
    }
  }

  static isAuthorized(routeType) {
    let userRoleType = cookies.get("user_role_type");

    if (routeType === "user") return true;

    if (routeType === "patient") return userRoleType === "PATIENT";

    if (routeType === "admin")
      return (
        userRoleType === "ORGANIZATION_ADMIN" ||
        userRoleType === "HOSPITAL_ADMIN"
      );

    return true;
  }

  static clearCookies() {
    TokenStorage.clearTokens();

    cookies.remove("start_view_path", { path: "/" });
    cookies.remove("user_role_type", { path: "/" });
    cookies.remove("user_roles", { path: "/" });
  }
}

export default Authenticator;
